import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Card, CardContent, Button, Grid, Chip,  
  TextField, RadioGroup, FormControlLabel, Radio, Divider,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../services/config';
import Header from '../../components/Header';

const UpgradePlan = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [paymentMethod, setPaymentMethod] = useState('mobile_money');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successDialog, setSuccessDialog] = useState(false);

  // Payment form state
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: '',
    cardName: '',
    cardExpiry: '',
    cardCVV: '',
    mobileNumber: '',
    bankName: ''
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/subscriptions`, {
        withCredentials: true
      });
      // Filter out Demo plan and sort by price
      const paidPlans = response.data.filter(p => p.name !== 'Demo').sort((a, b) => (a.price_fcfa || 0) - (b.price_fcfa || 0));
      setPlans(paidPlans);
      setError(null);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Impossible de charger les plans disponibles');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  const handlePaymentDetailChange = (field, value) => {
    setPaymentDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculatePrice = (basePrice) => {
    if (!basePrice) return 0;
    return billingCycle === 'annual' ? basePrice * 12 * 0.85 : basePrice; // 15% discount for annual
  };

  const handleSubmitPayment = async () => {
    if (!selectedPlan) {
      alert('Veuillez sélectionner un plan');
      return;
    }

    // Basic validation
    if (paymentMethod === 'credit_card') {
      if (!paymentDetails.cardNumber || !paymentDetails.cardName || !paymentDetails.cardExpiry || !paymentDetails.cardCVV) {
        alert('Veuillez remplir tous les détails de la carte');
        return;
      }
    } else if (paymentMethod === 'mobile_money') {
      if (!paymentDetails.mobileNumber) {
        alert('Veuillez entrer votre numéro de téléphone');
        return;
      }
    } else if (paymentMethod === 'bank_transfer') {
      if (!paymentDetails.bankName) {
        alert('Veuillez entrer le nom de votre banque');
        return;
      }
    }

    setSubmitting(true);
    
    // Simulate payment processing
    try {
      // In a real application, you would send payment details to your backend
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSuccessDialog(true);
      setSubmitting(false);
    } catch (err) {
      console.error('Payment error:', err);
      alert('Le paiement a échoué. Veuillez réessayer.');
      setSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessDialog(false);
    navigate('/preferences/billing');
  };

  const formatPrice = (price) => {
    return price ? price.toLocaleString('fr-FR') : '0';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/preferences/billing')} sx={{ mt: 2 }}>
          Retour
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Header 
        title="Améliorer Votre Plan"
        subtitle="Choisissez un plan qui correspond à vos besoins professionnels"
      />

      {/* Billing Cycle Toggle */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 2, alignItems: 'center' }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Cycle de facturation :
        </Typography>
        <RadioGroup 
          row 
          value={billingCycle} 
          onChange={(e) => setBillingCycle(e.target.value)}
        >
          <FormControlLabel 
            value="monthly" 
            control={<Radio />} 
            label="Mensuel" 
          />
          <FormControlLabel 
            value="annual" 
            control={<Radio />} 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Annuel
                <Chip label="Économisez 15%" size="small" color="success" sx={{ fontSize: 11, height: 20 }} />
              </Box>
            }
          />
        </RadioGroup>
      </Box>

      {/* Plan Selection */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.id}>
            <Card 
              sx={{ 
                height: '100%',
                border: selectedPlan?.id === plan.id ? '2px solid #1976d2' : '1px solid #e9ecef',
                boxShadow: selectedPlan?.id === plan.id ? '0 8px 24px rgba(25,118,210,0.2)' : '0 2px 8px rgba(0,0,0,0.08)',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                },
                position: 'relative'
              }}
              onClick={() => handlePlanSelect(plan)}
            >
              {plan.name === 'Pro' && (
                <Chip 
                  icon={<StarIcon sx={{ fontSize: 16 }} />}
                  label="POPULAR" 
                  size="small" 
                  sx={{ 
                    position: 'absolute', 
                    top: 16, 
                    right: 16, 
                    bgcolor: '#FFA726', 
                    color: 'white',
                    fontWeight: 700
                  }} 
                />
              )}
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {plan.name}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      {formatPrice(calculatePrice(plan.price_fcfa))}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#868e96' }}>
                      FCFA
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#868e96' }}>
                    par {billingCycle === 'annual' ? 'an' : 'mois'}
                  </Typography>
                  {billingCycle === 'annual' && plan.price_fcfa && (
                    <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 600 }}>
                      Économisez {formatPrice(plan.price_fcfa * 12 * 0.15)} FCFA/an
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: '#868e96', mb: 2 }}>
                  {plan.description || 'Parfait pour les entreprises en croissance'}
                </Typography>
                <Button 
                  variant={selectedPlan?.id === plan.id ? 'contained' : 'outlined'}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {selectedPlan?.id === plan.id ? 'Sélectionné' : 'Sélectionner'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Payment Form */}
      {selectedPlan && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Informations de paiement
            </Typography>

            {/* Payment Method Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Choisir le mode de paiement
              </Typography>
              <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Card 
                      sx={{ 
                        border: paymentMethod === 'mobile_money' ? '2px solid #1976d2' : '1px solid #e9ecef',
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#1976d2' }
                      }}
                      onClick={() => setPaymentMethod('mobile_money')}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel 
                          value="mobile_money" 
                          control={<Radio />} 
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneAndroidIcon />
                              <Typography>Mobile Money</Typography>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card 
                      sx={{ 
                        border: paymentMethod === 'credit_card' ? '2px solid #1976d2' : '1px solid #e9ecef',
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#1976d2' }
                      }}
                      onClick={() => setPaymentMethod('credit_card')}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel 
                          value="credit_card" 
                          control={<Radio />} 
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CreditCardIcon />
                              <Typography>Carte de crédit</Typography>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card 
                      sx={{ 
                        border: paymentMethod === 'bank_transfer' ? '2px solid #1976d2' : '1px solid #e9ecef',
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#1976d2' }
                      }}
                      onClick={() => setPaymentMethod('bank_transfer')}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel 
                          value="bank_transfer" 
                          control={<Radio />} 
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccountBalanceIcon />
                              <Typography>Virement bancaire</Typography>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </RadioGroup>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Payment Details Form */}
            {paymentMethod === 'credit_card' && (
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Numéro de carte"
                      placeholder="1234 5678 9012 3456"
                      value={paymentDetails.cardNumber}
                      onChange={(e) => handlePaymentDetailChange('cardNumber', e.target.value)}
                      InputProps={{
                        startAdornment: <CreditCardIcon sx={{ mr: 1, color: '#868e96' }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nom du titulaire"
                      placeholder="Jean Dupont"
                      value={paymentDetails.cardName}
                      onChange={(e) => handlePaymentDetailChange('cardName', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Date d'expiration"
                      placeholder="MM/AA"
                      value={paymentDetails.cardExpiry}
                      onChange={(e) => handlePaymentDetailChange('cardExpiry', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="CVV"
                      placeholder="123"
                      value={paymentDetails.cardCVV}
                      onChange={(e) => handlePaymentDetailChange('cardCVV', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {paymentMethod === 'mobile_money' && (
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Numéro de téléphone"
                  placeholder="+237 6XX XX XX XX"
                  value={paymentDetails.mobileNumber}
                  onChange={(e) => handlePaymentDetailChange('mobileNumber', e.target.value)}
                  InputProps={{
                    startAdornment: <PhoneAndroidIcon sx={{ mr: 1, color: '#868e96' }} />
                  }}
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                  Vous recevrez une demande de paiement sur votre téléphone. Veuillez l'approuver pour finaliser la transaction.
                </Alert>
              </Box>
            )}

            {paymentMethod === 'bank_transfer' && (
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Nom de la banque"
                  placeholder="Votre banque"
                  value={paymentDetails.bankName}
                  onChange={(e) => handlePaymentDetailChange('bankName', e.target.value)}
                  InputProps={{
                    startAdornment: <AccountBalanceIcon sx={{ mr: 1, color: '#868e96' }} />
                  }}
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                  Les détails du virement bancaire vous seront envoyés par email après confirmation. Le traitement peut prendre 1 à 3 jours ouvrables.
                </Alert>
              </Box>
            )}

            {/* Order Summary */}
            <Card sx={{ bgcolor: '#f8f9fa', p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Résumé de la commande
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Plan :</Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedPlan.name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Cycle de facturation :</Typography>
                <Typography sx={{ fontWeight: 600 }}>{billingCycle === 'annual' ? 'Annuel' : 'Mensuel'}</Typography>
              </Box>
              {billingCycle === 'annual' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: '#10B981' }}>
                  <Typography>Réduction (15%) :</Typography>
                  <Typography sx={{ fontWeight: 600 }}>-{formatPrice((selectedPlan.price_fcfa || 0) * 12 * 0.15)} FCFA</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Total :</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {formatPrice(calculatePrice(selectedPlan.price_fcfa))} FCFA
                </Typography>
              </Box>
            </Card>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/preferences/billing')}
                disabled={submitting}
              >
                Annuler
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSubmitPayment}
                disabled={submitting}
                sx={{ minWidth: 150 }}
              >
                {submitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Payer ${formatPrice(calculatePrice(selectedPlan.price_fcfa))} FCFA`
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {!selectedPlan && (
        <Alert severity="info" sx={{ mt: 3 }}>
          Veuillez sélectionner un plan ci-dessus pour continuer le processus de mise à niveau.
        </Alert>
      )}

      {/* Success Dialog */}
      <Dialog open={successDialog} onClose={handleSuccessClose}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: '#10B981', fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Paiement réussi !
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Votre mise à niveau vers le plan <strong>{selectedPlan?.name}</strong> a été effectuée avec succès.
            Vous avez maintenant accès à toutes les fonctionnalités premium !
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleSuccessClose}>
            Aller à la facturation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpgradePlan;

